import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";
import {
  getEnduserSessionDetailForGivenProject,
  getEnduserCurrentProjectGroupCompletion,
  getEnduserProjectGroupsCompletion,
} from "../../features/enduserStats/state/enduserStatsSlice";
import { EmptyState } from "../../features/statsNew/components";

const EnduserSessionDetailView = () => {
  const { t } = useTranslation();
  const { parcoursId, moduleId, sessionId } = useParams<{
    parcoursId: string;
    moduleId: string;
    sessionId: string;
  }>();

  const projectGroupsCompletion = useSelector(getEnduserProjectGroupsCompletion);
  const currentProjectGroupCompletion = useSelector(getEnduserCurrentProjectGroupCompletion);
  const sessionDetail = useSelector(getEnduserSessionDetailForGivenProject);

  // Find parcours info
  const parcoursInfo = useMemo(() => {
    return projectGroupsCompletion.find((pg) => pg.projectGroupId === Number(parcoursId));
  }, [projectGroupsCompletion, parcoursId]);

  // Find module info
  const moduleInfo = useMemo(() => {
    return currentProjectGroupCompletion.find((m) => m.projectId === Number(moduleId));
  }, [currentProjectGroupCompletion, moduleId]);

  // Find session info by index (since we don't have ID in the interface)
  const sessionIndex = Number(sessionId);
  const sessionInfo = sessionDetail?.sessions?.[sessionIndex];

  const formatDuration = (seconds: number | undefined): string => {
    if (!seconds) return "-";
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (!sessionInfo) {
    return (
      <EmptyState
        icon="calendar"
        title={t("statsNew.session.noSessions")}
        description={t("statsNew.emptyState.noSessionsDescription")}
      />
    );
  }

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Breadcrumb */}
      <nav className="stats-breadcrumb mb-2">
        <Link to="/stats/parcours">{t("enduserStats.tabs.parcours")}</Link>
        <span className="stats-breadcrumb-separator">/</span>
        <Link to={`/stats/parcours/${parcoursId}`}>{parcoursInfo?.projectGroupName}</Link>
        <span className="stats-breadcrumb-separator">/</span>
        <Link to={`/stats/parcours/${parcoursId}/${moduleId}`}>{moduleInfo?.projectName}</Link>
        <span className="stats-breadcrumb-separator">/</span>
        <span className="stats-breadcrumb-current">
          {t("statsNew.session.session")} #{Number(sessionId) + 1}
        </span>
      </nav>

      {/* Header */}
      <div className="flex-shrink-0 bg-white rounded-lg border border-gray-200 px-4 py-3 mb-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">
              {t("statsNew.session.session")} #{Number(sessionId) + 1}
            </h2>
            <p className="text-sm text-gray-500">{formatDate(sessionInfo.createdAt)}</p>
          </div>
          {sessionInfo.progressPercentage === 100 ? (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-800">
              {t("statsNew.session.completed")}
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
              {t("statsNew.session.incomplete")}
            </span>
          )}
        </div>
      </div>

      {/* Session Stats */}
      <div className="flex-shrink-0 grid grid-cols-2 md:grid-cols-3 gap-3 mb-3">
        {/* Score */}
        {sessionDetail?.isProjectScorable && sessionInfo.score && (
          <div className="bg-white rounded-lg border border-gray-200 p-3">
            <div className="text-sm text-gray-500">{t("statsNew.interactions.score")}</div>
            <div className="text-xl font-bold text-gray-900">{sessionInfo.score}</div>
          </div>
        )}

        {/* Duration */}
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-sm text-gray-500">{t("statsNew.interactions.duration")}</div>
          <div className="text-xl font-bold text-gray-900">
            {formatDuration(sessionInfo.duration)}
          </div>
        </div>

        {/* Completion */}
        <div className="bg-white rounded-lg border border-gray-200 p-3">
          <div className="text-sm text-gray-500">{t("statsNew.module.completionRate")}</div>
          <div className="text-xl font-bold text-gray-900">
            {sessionInfo.progressPercentage ?? 0}%
          </div>
        </div>
      </div>

      {/* Back to module button */}
      <div className="flex-shrink-0 mt-3">
        <Link
          to={`/stats/parcours/${parcoursId}/${moduleId}`}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          {t("enduserStats.backToModule")}
        </Link>
      </div>
    </div>
  );
};

export default EnduserSessionDetailView;
